import { defineStore } from 'pinia';
import _ from 'lodash';

import { connectPrimus } from '@/utils/primus';
import {
  sortUpdated,
  sortOffline,
  chatStatus,
  incomeTypes,
  statusTypes,
  agentForOrganizationStatusTypes,
  extractMsgText,
  toProperMsgFormat,
  normalizeLanguageCode,
} from '@/utils/livechat';
import LivechatService from '@/services/livechat';
import TranslationService from '@/services/translation';
import DialogService from '@/services/dialogs';
import BotFormsService from '@/services/botForms';
import ApiProtocolError from '@/errors/ApiProtocolError';
import i18n from '@/i18n';
import { useAuthStore } from '@/stores/auth';
import { useBotsStore } from '@/stores/bots';
import { useHubUiStore } from '@/stores/hubUi';
import { useInactivityTrackerStore } from '@/stores/inactivityTracker';
import { useNotificationNativeStore } from '@/stores/notificationNative';
import { useAudioStore } from '@/stores/audio';

/** Builds the `hubUi/addAlert`-shaped payload from a caught error (kept module-scope, exactly as legacy). */
function prepareAlertFromError(error: any, message: string | null = null) {
  let errorMessage = message || error.message;
  if (error instanceof ApiProtocolError) {
    errorMessage = `${error.code}: ${i18n.global.t(`livechat.errorMsgs.${error.code}`)}`;
  }
  return {
    message: errorMessage,
    type: 'error' as const,
    scope: 'global',
  };
}

/** Live Primus connection instance. Module-scope singleton, exactly as the legacy module (shared regardless of how many times the store is `use`d). */
let primus: any = null;

/**
 * In-flight primus connection. `init()` can run twice concurrently (App.vue starts one on bot load
 * while the Livechat view mounts and starts another), and both would pass the `!primus` check
 * before either has a socket. Sharing the pending connection keeps it to one socket.
 */
let primusConnecting: Promise<any> | null = null;

/**
 * Bumped by `goOffline()`. A connection still in flight when the agent goes offline belongs to an
 * older generation, so it is closed on arrival instead of being adopted as the live socket.
 */
let primusGeneration = 0;
let primusConnectingGeneration = 0;

const tabIdSessionKey = 'moin.livechat.tabIdSession';

const randomString = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

/** Cross-tab broadcast channel singleton, module-scope exactly as legacy. */
let broadcastChannel: BroadcastChannel | null = null;

const getFromStorage = (key: string, defaultValue: any, storage: 'local' | 'session' = 'local') => {
  const storageValue = (window as any)[`${storage}Storage`].getItem(key);
  if (storageValue) {
    try {
      return JSON.parse(storageValue);
    } catch (error) {
      // value is not json
      return storageValue;
    }
  }
  return defaultValue;
};

const setToStorage = (key: string, value: any, storage: 'local' | 'session' = 'local') => {
  (window as any)[`${storage}Storage`].setItem(key, JSON.stringify(value));
};

/** State of the livechat module */
interface LivechatState {
  loading: boolean;
  /** primus initialized */
  primusInitialized: boolean;
  uniqueBotId: string | null;
  /** bot livechat setting is enabled and configured */
  botHasLivechat: boolean;
  activeTabId: string | null;
  currentTabId: string | null;
  /** current user agent object */
  agent: any;
  /** user is either an agent for the current organization, an agent for another organization or not an agent at all */
  agentForOrganizationStatus: string | null;
  organisation: any;
  isChatOpen: boolean;
  isLivechatVisible: boolean;
  activeChatId: string | null;
  chatsOtherAgents: Record<string, any>;
  chatsOpen: Record<string, any>;
  chatsUnassigned: Record<string, any>;
  chatsClosed: Record<string, any>;
  /**
   * Local Store Object to track important chat changes
   * Key is chat.uuid. Value (object) can hold any info the logic needs related to the chat.
   */
  localChatsInfo: Record<string, { isMessageNew?: boolean; [key: string]: any }>;
  activeTabTicker: any;
  chatHistories: Record<string, any>;
  isTabVisible: boolean;
  chatTranslations: Record<string, string>;
  agentLanguages: string[];
  /** Detected language of the active conversation; undefined when the chat carries none. */
  activeChatLanguage: string | undefined;
  hubSettings: {
    soundNewMessage: string;
    soundNewChat: string;
    volume: number;
    notificationAudio: boolean;
    notificationBrowser: boolean;
  };
  translationServiceError: boolean;
  isAgentInactive: boolean;
  /** a flag to control the closed by supervisor dialog */
  isClosedBySupervisorOpen: boolean;
  /** list of other online agents */
  onlineAgents: any[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/livechat.ts` (namespace `livechat`).
 * Persisted to BOTH `sessionStorage` and `localStorage` (full state, session wins on hydrate) —
 * matches the legacy module being listed in both the `vuexLocal` and `vuexSession` reducers in
 * `src/store/index.ts`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Pinia merges state and getters onto a single `this`. Legacy had these state-prop/getter
 *   name collisions — the STATE PROP keeps its name, the GETTER is renamed `<name>Getter`:
 *   `loading`, `primusInitialized`, `botHasLivechat`, `agent`, `agentForOrganizationStatus`,
 *   `isChatOpen`, `onlineAgents`, `chatTranslations`, `translationServiceError`, `isAgentInactive`
 *   (all pure identity passthroughs); plus `chatsOtherAgents`, `chatsUnassigned`, `chatsOpen`,
 *   `chatsClosed` (NOT pure passthroughs — the getters map/sort the state's object map into an
 *   array, but the name still collides so the getter is renamed the same way, exactly like
 *   `bots.ts`'s `currentBot` -> `currentBotGetter`); and `activeChatLanguage` (the getter reads
 *   the active chat's detected `conversationLanguage`, not the state prop).
 * - Legacy action `setIsLivechatVisible` committed the mutation of the same name AND did
 *   extra work (marking the active chat read) — merged into a single action.
 * - All other legacy mutations (no action of the same name existed) become same-named actions:
 *   `setValue`, `setIsChatOpen`, `setCurrentTabId`, `setActiveTabId`, `setAgent`,
 *   `setAgentForOrganizationStatus`, `setOnlineAgents`, `setChatsUnassigned`, `setChatsOpen`,
 *   `setChatsOtherAgents`, `setChatsClosed`, `setChatTranslations`, `addTranslatedMessage`,
 *   `resetLivechatChats`, `resetLivechat`, `setLocalChatsInfo`, `removeLocalChatInfo`,
 *   `setIsClosedBySupervisor`.
 * - `livechatProfile` getter reads `state.livechatProfile`, a property that was never part of
 *   this module's state object — kept verbatim as intentionally-preserved dead/buggy code
 *   (always returns `undefined`, exactly as in the legacy module).
 * - Cross-namespace access, resolved via sibling stores (called INSIDE the getter/action body):
 *   `rootGetters['auth/isSupervisor']` -> `useAuthStore().isSupervisor`;
 *   `rootGetters['auth/user']` -> `useAuthStore().userGetter` (legacy `auth` getter `user` was
 *   renamed `userGetter` there, see `auth.ts`);
 *   `rootGetters['bots/getBotSettings'](id)` -> `useBotsStore().getBotSettings(id)`;
 *   `rootGetters['bots/currentChannels']` -> `useBotsStore().currentChannels`;
 *   `rootGetters['bots/getBot'](id)` -> `useBotsStore().getBot(id)`;
 *   `rootGetters['bots/currentBot']` -> `useBotsStore().currentBotGetter` (legacy `bots` getter
 *   `currentBot` was renamed `currentBotGetter` there, see `bots.ts`);
 *   `commit('hubUi/addAlert', payload, {root:true})` -> `useHubUiStore().addAlert(payload)`;
 *   `commit('inactivityTracker/setTimeoutDuration', p, {root:true})` ->
 *   `useInactivityTrackerStore().setTimeoutDuration(p)`;
 *   `dispatch('inactivityTracker/restartTimeoutTimer', null, {root:true})` ->
 *   `useInactivityTrackerStore().restartTimeoutTimer()`;
 *   `dispatch('inactivityTracker/removeTimeoutTimer', null, {root:true})` ->
 *   `useInactivityTrackerStore().removeTimeoutTimer()`;
 *   `store.dispatch('notificationNative/initNotificationNative')` ->
 *   `useNotificationNativeStore().initNotificationNative()`;
 *   `dispatch('notificationNative/showNotificationNative', p, {root:true})` ->
 *   `useNotificationNativeStore().showNotificationNative(p)`;
 *   `dispatch('audio/playAudio', p, {root:true})` -> `useAudioStore().playAudio(p)`.
 */
export const useLivechatStore = defineStore('livechat', {
  state: (): LivechatState => ({
    loading: false,
    primusInitialized: false, // primus initialized
    uniqueBotId: null,
    botHasLivechat: false, // bot livechat setting is enabled and configured
    activeTabId: null,
    currentTabId: null,
    agent: null, // current user agent object
    agentForOrganizationStatus: null, // user is either an agent for the current organization, an agent for another organization or not an agent at all
    organisation: null,
    isChatOpen: false,
    isLivechatVisible: false,
    activeChatId: null,
    chatsOtherAgents: {},
    chatsOpen: {},
    chatsUnassigned: {},
    chatsClosed: {},
    localChatsInfo: {}, // Object to track chat changes
    activeTabTicker: null,
    chatHistories: {},
    isTabVisible: true,
    chatTranslations: {},
    agentLanguages: ['DE'],
    activeChatLanguage: 'EN',
    hubSettings: {
      soundNewMessage: '/sounds/blooom.mp3',
      soundNewChat: '/sounds/din-don.mp3',
      volume: 0.5,
      notificationAudio: false,
      notificationBrowser: true,
    },
    translationServiceError: false,
    isAgentInactive: false,
    isClosedBySupervisorOpen: false, // a flag to control the closed by supervisor dialog
    onlineAgents: [], // list of other online agents
  }),
  getters: {
    /** Renamed from legacy identity-passthrough getter `loading` (collides with state prop). */
    loadingGetter(state): boolean {
      return state.loading;
    },
    /** Renamed from legacy identity-passthrough getter `primusInitialized` (collides with state prop). */
    primusInitializedGetter(state): boolean {
      return state.primusInitialized;
    },
    /** Renamed from legacy identity-passthrough getter `botHasLivechat` (collides with state prop). */
    botHasLivechatGetter(state): boolean {
      return state.botHasLivechat;
    },
    isCurrentTabActive(state): boolean {
      return state.activeTabId === state.currentTabId;
    },
    /** Renamed from legacy identity-passthrough getter `agent` (collides with state prop). */
    agentGetter(state): any {
      return state.agent;
    },
    agentStatus(): string | undefined {
      if (!this.agent) return;

      if (this.isCurrentTabActive) {
        return statusTypes.online;
      } else if (this.activeTabId) {
        return statusTypes.otherTab;
      } else {
        return statusTypes.offline;
      }
    },
    /** Renamed from legacy identity-passthrough getter `agentForOrganizationStatus` (collides with state prop). */
    agentForOrganizationStatusGetter(state): string | null {
      return state.agentForOrganizationStatus;
    },
    /**
     * Legacy getter reading `state.livechatProfile` — kept verbatim as intentionally-preserved
     * dead/buggy code. `livechatProfile` was never part of this module's state, so this always
     * returned (and still returns) `undefined`.
     */
    livechatProfile(state): any {
      return (state as any).livechatProfile;
    },
    /** Renamed from legacy identity-passthrough getter `isChatOpen` (collides with state prop). */
    isChatOpenGetter(state): boolean {
      return state.isChatOpen;
    },
    /** Renamed from legacy identity-passthrough getter `onlineAgents` (collides with state prop). */
    onlineAgentsGetter(state): any[] {
      return state.onlineAgents;
    },
    /**
     * Renamed from legacy getter `chatsOtherAgents` (collides with the state prop of the same
     * name; NOT a pure passthrough — maps the state's object map to an array).
     */
    chatsOtherAgentsGetter(state): any[] {
      return Object.values(state.chatsOtherAgents);
    },
    // Net line has nothing to deal with changeToChat
    activeChat(): any {
      const isSupervisor = useAuthStore().isSupervisor;

      let chat =
        this.chatsOpen[this.activeChatId as any] ||
        this.chatsUnassigned[this.activeChatId as any] ||
        this.chatsClosed[this.activeChatId as any] ||
        null;

      // For Supervisor also check 'others' and 'closed' chats
      if (!chat && isSupervisor) {
        chat = this.chatsOtherAgents[this.activeChatId as any] || this.chatsClosed[this.activeChatId as any] || null;
      }

      return chat;
    },
    inSupervisorMode(): boolean {
      // Conditions to supervisor mode:
      //  - User has 'supervisor' feature
      //  - Is 'offline' as an agent
      const isSupervisor = useAuthStore().isSupervisor;
      const isOffline = this.agentStatus === statusTypes.offline;

      return isSupervisor && isOffline;
    },
    /**
     * Renamed from legacy getter `chatsUnassigned` (collides with the state prop of the same
     * name; NOT a pure passthrough — sorts and returns an array).
     */
    chatsUnassignedGetter(state): any[] {
      const chats = Object.values(state.chatsUnassigned);
      chats.sort(sortUpdated);
      // sortOffline intentionally returns `undefined` in one branch (pre-existing utility
      // behavior, tolerated by Array#sort at runtime under Vuex's untyped `any` context).
      chats.sort(sortOffline as (a: any, b: any) => number);

      return chats;
    },
    /**
     * Renamed from legacy getter `chatsOpen` (collides with the state prop of the same name;
     * NOT a pure passthrough — sorts and returns an array).
     */
    chatsOpenGetter(state): any[] {
      const chats = Object.values(state.chatsOpen);
      chats.sort(sortUpdated);
      // sortOffline intentionally returns `undefined` in one branch (pre-existing utility
      // behavior, tolerated by Array#sort at runtime under Vuex's untyped `any` context).
      chats.sort(sortOffline as (a: any, b: any) => number);
      return chats;
    },
    /**
     * Renamed from legacy getter `chatsClosed` (collides with the state prop of the same name;
     * NOT a pure passthrough — returns an array).
     */
    chatsClosedGetter(state): any[] {
      return Object.values(state.chatsClosed);
    },
    incomeType(): string | null {
      if (!this.isCurrentTabActive) {
        return null;
      }
      if (Object.keys(this.chatsUnassigned).length > 0) {
        return incomeTypes.newChat;
      }

      let hasNewMessages = Object.values(this.chatsOpen as Record<string, any>).some((c: any) => {
        if (c.uuid === this.activeChatId && this.activeChatIsVisible) {
          //if the chat with the new message is open, don't show the notification
          return false;
        }

        return c.isMessageNew;
      });
      if (hasNewMessages) {
        return incomeTypes.newMessage;
      }
      return incomeTypes.activeTab;
    },
    chatHistoryById(state) {
      return (chatUuid: string) => state.chatHistories[chatUuid];
    },
    activeChatIsVisible(state): boolean {
      return state.isLivechatVisible && state.isTabVisible && state.isChatOpen;
    },
    /** Renamed from legacy identity-passthrough getter `chatTranslations` (collides with state prop). */
    chatTranslationsGetter(state): Record<string, string> {
      return state.chatTranslations;
    },
    /**
     * Both translation engines are driven from here so preview, history and the sent message can
     * never end up on different ones. Configurable per bot in moin_livechat settings.
     */
    useDeepL(state): boolean {
      const botSettings = useBotsStore().getBotSettings(state.uniqueBotId as any);
      return botSettings?.moin_livechat?.useDeepL === true;
    },
    useTranslation(state): boolean {
      // If auto-translate is not explicitly enabled in bot settings, do not use it
      const botSettings = useBotsStore().getBotSettings(state.uniqueBotId as any);
      if (!botSettings?.moin_livechat?.autoTranslate) {
        return false;
      }
      // Else translate if we can find the language key
      if (!state.activeChatLanguage) return false;
      return !state.agentLanguages.map((l) => l.toLowerCase()).includes(state.activeChatLanguage.toLowerCase());
    },
    /**
     * Renamed from legacy getter `activeChatLanguage` (collides with the state prop of the same
     * name). Reads the language detected for the conversation off the active chat rather than the
     * state prop, so the header and the input translation follow the visitor's actual language
     * instead of the channel default.
     */
    activeChatLanguageGetter(): string {
      return normalizeLanguageCode(this.activeChat?.conversationLanguage)?.toUpperCase() || '';
    },
    activeChatGroups(): any[] {
      const chatGroups = this.activeChat?.groups || [];
      return _.intersection(chatGroups, this.agent?.groups || []);
    },
    organization(state): any {
      return state.organisation;
    },
    shortcodeContext(state): { name: any } {
      return {
        name: state?.agent?.name,
      };
    },
    activeChatFiles(state): any[] {
      if (!state.organisation) return [];
      // Files from groups
      const groupFiles = state.organisation.groups
        .filter((g: any) => this.activeChatGroups.includes(g.uuid))
        .map(
          (g: any) =>
            g.files?.map((f: any) => ({
              ...f,
              group: g.name,
            })) || [],
        )
        .flat();
      // Organization-level files
      const orgFiles = state.organisation.files || [];
      return [...groupFiles, ...orgFiles];
    },
    activeChatShortcodes(state): any[] {
      if (!state.organisation) return [];
      // Shortcodes from groups
      const groupShortcodes = state.organisation.groups
        .filter((g: any) => this.activeChatGroups.includes(g.uuid))
        .map((g: any) => {
          return (
            g.shortcodes?.map((s: any) => ({
              ...s,
              group: g.name,
            })) || []
          );
        })
        .flat();
      // Global organization shortcodes
      const globalShortcodes = state.organisation.shortcodes || [];
      return [...groupShortcodes, ...globalShortcodes];
    },
    getLocalChatsInfo(state): Record<string, any> {
      return state.localChatsInfo;
    },
    /** Renamed from legacy identity-passthrough getter `translationServiceError` (collides with state prop). */
    translationServiceErrorGetter(state): boolean {
      return state.translationServiceError;
    },
    defaultHubSettings(state): any {
      return state.hubSettings;
    },
    activeHubSettings(state): any {
      return state.agent?.hubSettings || state.hubSettings;
    },
    /** Renamed from legacy identity-passthrough getter `isAgentInactive` (collides with state prop). */
    isAgentInactiveGetter(state): boolean {
      return state.isAgentInactive;
    },
  },
  actions: {
    async init({ botId, routePath }: { botId: string | null; routePath?: string | null }) {
      this.loading = true;
      this.resetLivechat();

      if (!botId) {
        // console.log('livechat 🤌 – no init, because of no botId');
        return false;
      }

      if (routePath && routePath.includes('/preview')) {
        // console.log('livechat 🤌 – no init in preview');
        return false;
      }

      // clearInterval(state.activeTabTicker);

      // check if bot setting is set, call getter getBotSettings from bots store
      const botSettings = useBotsStore().getBotSettings(botId);

      if (!botSettings.moin_livechat) {
        this.botHasLivechat = false;
        return false;
      }

      let botHasLivechatResponse = null;
      try {
        botHasLivechatResponse = await LivechatService.getBotHasLivechat(botId);
      } catch (e) {
        useHubUiStore().addAlert(prepareAlertFromError(e));
      }

      const { exists, uuid } = botHasLivechatResponse || ({} as any);

      this.botHasLivechat = exists || false;

      if (!this.botHasLivechat) {
        return false;
      }

      //set initial agent language
      this.agentLanguages = [useAuthStore().userGetter.language];

      // load agent
      await this.loadAgent({ orgUuid: uuid });
      if (this.agentForOrganizationStatus === agentForOrganizationStatusTypes.isNotAgent) {
        return;
      } else if (this.agentForOrganizationStatus === agentForOrganizationStatusTypes.isAgentForCurrentOrganization) {
        // load organization
        this.organisation = await LivechatService.getOrganizatioByUuid(uuid);
      }

      // Set tab check interval
      // state.activeTabTicker = setInterval(async () => {
      //   await dispatch('checkIsCorrectTab');
      //   dispatch('checkIncomeType');
      // }, tabCheckInterval);

      // this.resetLivechat(); moved to top
      this.uniqueBotId = botId;

      this.loadTabId();
      this.initBroadcastChannel();

      if (this.agentStatus === statusTypes.online) {
        await this.initChat();
        await this.loadOnlineAgents();
      }
      if (this.organisation?.timeoutLimit) {
        useInactivityTrackerStore().setTimeoutDuration({
          timeoutDuration: this.organisation?.timeoutLimit * 1000 * 60,
        });
        useInactivityTrackerStore().restartTimeoutTimer();
      }

      this.loading = false;
    },

    initBroadcastChannel() {
      if (!broadcastChannel) {
        broadcastChannel = new BroadcastChannel('moin-livechat');
      }

      broadcastChannel.onmessage = (ev) => {
        this.handleBroadcastChannelMessage(ev);
      };
    },

    handleVisibilityChange() {
      // handles tab visibility changes
      this.isTabVisible = document.visibilityState === 'visible' && document.hasFocus();
      if (this.activeChatIsVisible && this.activeChatId) {
        // if the tab is visible and the chat is open, set the active chat to read
        if (this.chatsOpen[this.activeChatId]) {
          this.chatsOpen[this.activeChatId].isMessageNew = false;
          this.setLocalChatsInfo({ chat: this.activeChatId, data: { isMessageNew: false } });
        }
      }
    },
    /**
     * Re-fetches the organization (including its files/groups) so that files uploaded/removed
     * elsewhere (e.g. LiveChat Management) are reflected here. The organization is otherwise only
     * loaded once during init(), which left activeChatFiles stale after uploads made outside of
     * this store.
     */
    async refreshOrganization() {
      if (!this.organisation?.uuid) return;
      try {
        const organisation = await LivechatService.getOrganizatioByUuid(this.organisation.uuid);
        if (organisation) this.organisation = organisation;
      } catch (error) {
        console.error('Failed to refresh organization:', error);
      }
    },
    async initChat() {
      useNotificationNativeStore().initNotificationNative();

      await this.loadChats();
      if (!primus) await this.initPrimus();
    },
    async initPrimus() {
      if (!this.agent?.uuid) {
        this.primusInitialized = false;
      }
      if (primusConnecting) {
        // Another init() is already connecting — wait for it instead of opening a second socket.
        const cancelled = primusConnectingGeneration !== primusGeneration;
        await primusConnecting;
        // That attempt was cancelled by goOffline() and gets discarded, so start a fresh one.
        if (cancelled && !primus) await this.initPrimus();
        return;
      }

      const apiUrl = import.meta.env.VITE_LIVECHAT_API_URL;
      const query = `agentUuid=${this.agent.uuid}`;
      const generation = primusGeneration;
      primusConnectingGeneration = generation;
      primusConnecting = connectPrimus({ apiUrl, query });
      let socket: any;
      try {
        socket = await primusConnecting;
      } finally {
        primusConnecting = null;
      }

      if (generation !== primusGeneration) {
        // The agent went offline while this was connecting — don't leave them connected.
        socket?.end();
        return;
      }

      primus = socket;

      if (!primus) {
        // The client script could not be loaded; connectPrimus() has already logged why.
        this.primusInitialized = false;
        return;
      }

      primus.on('open', (ev: any) => console.log('Connected to primus server', apiUrl));
      primus.on('error', (err: any) => console.log(err));
      primus.on('end', (ev: any) => console.log('Primus connection ended', ev));
      primus.on('reconnect', (ev: any) => console.log('Primus reconnect', ev));
      primus.on('reconnect scheduled', (ev: any) => console.log('Primus reconnect scheduled', ev));
      primus.on('reconnected', (ev: any) => console.log('Primus reconnected', ev));

      const handleData = (data: any) => {
        // console.log('handleData() - Received primus data', data);
        if (!this.isCurrentTabActive) {
          return;
        }
        switch (data.type) {
          case 'message':
            this.addMessage(data);
            break;
          case 'chat_created':
            this.addCreatedChat(data);
            break;
          case 'chat_assigned':
            this.assignChat(data);
            break;
          case 'chat_reassigned':
            this.reassignChat(data);
            break;
          case 'user_disconnected':
            this.userStatusChanged(data);
            break;
          case 'chat_closed':
            this.handleChatClosedBySupervisor({ uuid: data.chatUuid });
            break;
          case 'user_connected':
            this.userStatusChanged(data);
            break;
          case 'agent_online':
            this.handleAgentOnline(data);
            break;
          case 'agent_offline':
            this.handleAgentOffline(data);
            break;
          default:
            console.error('Unknown primus data type');
        }
      };

      primus.on('data', handleData);

      this.primusInitialized = true;
    },
    async loadAgent({ orgUuid }: { orgUuid: string }) {
      try {
        const agentObj = await LivechatService.getAgentByToken();

        if (agentObj.organizationUuid === orgUuid) {
          this.setAgent(agentObj);
          this.setAgentForOrganizationStatus(agentForOrganizationStatusTypes.isAgentForCurrentOrganization);
        } else {
          this.setAgentForOrganizationStatus(agentForOrganizationStatusTypes.isAgentForOtherOrganization);
        }
      } catch (error) {
        this.setAgentForOrganizationStatus(agentForOrganizationStatusTypes.isNotAgent);
      }
    },
    async loadOnlineAgents() {
      if (!this.agent?.uuid) {
        return;
      }

      try {
        const organizationUuid = this.organisation?.uuid;
        if (!organizationUuid) {
          return;
        }

        const result = await LivechatService.getOnlineAgents(organizationUuid);

        // Filter out current agent
        const otherAgents = (result || []).filter((agent: any) => agent.uuid !== this.agent?.uuid);

        this.setOnlineAgents(otherAgents);
      } catch (error) {
        console.error('Error loading online agents:', error);
        this.setOnlineAgents([]);
      }
    },
    async loadChats() {
      if (!this.agent?.uuid || !this.uniqueBotId) {
        return false;
      }

      this.resetLivechatChats();

      const chats = await LivechatService.getAllChats({
        agentUuid: this.agent.uuid,
        uniqueBotId: this.uniqueBotId,
      });

      this.prepareOpenChats(chats.open);
      this.prepareUnassignedChats(chats.unassigned);
      this.prepareClosedChats(chats.closed);

      /**
       * Start loading last used forms, prioritizing unassigned, then open.
       * Closed & other agents can be ignored, unless you need those.
       */

      for (const id in this.chatsUnassigned) {
        this.populateCompletedForm(this.chatsUnassigned[id]).then((lastForm: any) => {
          this.setChatsUnassigned({ ...this.chatsUnassigned, [id]: { ...this.chatsUnassigned[id], lastForm } });
        });
      }

      for (const id in this.chatsOpen) {
        this.populateCompletedForm(this.chatsOpen[id]).then((lastForm: any) => {
          this.setChatsOpen({ ...this.chatsOpen, [id]: { ...this.chatsOpen[id], lastForm } });
        });
      }
    },

    /**
     * Fetch Chats for Organization. Is used for Supervisors
     * @returns
     */
    async loadChatsOrganization() {
      const organizationUuid = this.organisation?.uuid;

      if (!organizationUuid) return false;

      this.resetLivechatChats();
      // If we set status to 'all' then backend return already formatted { open: [], closed: [], unassigned: [] }
      let chats: any;
      try {
        chats = await LivechatService.getAllChatsOrganization({
          organizationUuid,
          status: 'all',
        });
      } catch (error) {
        console.error('Something went wrong when fetching chats for organization. Error:', error);
        return false;
      }

      if (!chats || chats.error) return false;

      const agentChats = (chats.open || []).filter((chat: any) => chat.agent === this.agent?._id);
      // const otherAgentChats = chats.open.filter(chat => chat.agent !== state.agent?._id);

      this.prepareOpenChats(agentChats);
      this.prepareUnassignedChats(chats.unassigned || []);
      this.prepareClosedChats(chats.closed || []);
      this.prepareOtherAgentsChats(chats.open || []);

      /**
       * Start loading last used forms, prioritizing unassigned, then open.
       * Closed & other agents can be ignored, unless you need those.
       */
      for (const id in this.chatsUnassigned) {
        this.populateCompletedForm(this.chatsUnassigned[id]).then((lastForm: any) => {
          this.setChatsUnassigned({ ...this.chatsUnassigned, [id]: { ...this.chatsUnassigned[id], lastForm } });
        });
      }

      for (const id in this.chatsOpen) {
        this.populateCompletedForm(this.chatsOpen[id]).then((lastForm: any) => {
          this.setChatsOpen({ ...this.chatsOpen, [id]: { ...this.chatsOpen[id], lastForm } });
        });
      }

      for (const id in this.chatsOtherAgents) {
        this.populateCompletedForm(this.chatsOtherAgents[id]).then((lastForm: any) => {
          this.setChatsOtherAgents({
            ...this.chatsOtherAgents,
            [id]: { ...this.chatsOtherAgents[id], lastForm },
          });
        });
      }

      for (const id in this.chatsClosed) {
        this.populateCompletedForm(this.chatsClosed[id]).then((lastForm: any) => {
          this.setChatsClosed({ ...this.chatsClosed, [id]: { ...this.chatsClosed[id], lastForm } });
        });
      }
    },
    prepareOpenChats(chats: any[]) {
      const localChatsInfo = this.getLocalChatsInfo;

      chats.forEach((c) => {
        const { isMessageNew } = localChatsInfo[c.uuid] || {};
        c.isMessageNew = isMessageNew ?? false;
      });

      const currentChannels = useBotsStore().currentChannels;

      if (currentChannels.length > 1) {
        // Find channels
        chats.forEach((chat) => {
          if (!currentChannels.length) return;
          const channelSlug = chat.uniqueUserId.split('.')[1];
          const channel = currentChannels.find((c: any) => c.channel === channelSlug);

          if (channel) {
            chat.channel = {
              id: channel.channelId,
              slug: channel.channel,
              displayName: channel.displayName,
              language: channel.languages[0] || 'de',
            };
          }
        });
      }

      const chatsMap = chats.reduce((map: any, chat: any) => {
        chat.conversationLanguage = normalizeLanguageCode(chat.conversationLanguage);
        map[chat.uuid] = chat;
        return map;
      }, {});

      this.setChatsOpen(chatsMap);
      return true;
    },
    prepareUnassignedChats(chats: any[]) {
      const chatsMap = chats.reduce((map: any, chat: any) => {
        chat.conversationLanguage = normalizeLanguageCode(chat.conversationLanguage);
        map[chat.uuid] = chat;
        return map;
      }, {});

      this.setChatsUnassigned(chatsMap);
      return true;
    },
    async loadClosedChats() {
      if (!this.agent?.uuid || !this.uniqueBotId) {
        return false;
      }
      const chats = await LivechatService.getClosedChats({
        agentUuid: this.agent.uuid,
        uniqueBotId: this.uniqueBotId,
      });

      this.prepareClosedChats(chats);
    },
    prepareClosedChats(chats: any[]) {
      const chatsMap = chats.reduce((map: any, chat: any) => {
        chat.conversationLanguage = normalizeLanguageCode(chat.conversationLanguage);
        map[chat.uuid] = chat;
        return map;
      }, {});

      this.setChatsClosed(chatsMap);
      return true;
    },
    prepareOtherAgentsChats(chats: any[]) {
      const chatsMap = chats.reduce((map: any, chat: any) => {
        chat.conversationLanguage = normalizeLanguageCode(chat.conversationLanguage);
        map[chat.uuid] = chat;
        return map;
      }, {});

      this.setChatsOtherAgents(chatsMap);
      return true;
    },
    async addCreatedChat({ chatUuid, data, groups }: { chatUuid: string; data: string; groups: any }) {
      // if the chatUuid is in the open chats -> the chat is reassigned to group -> clean up the chat
      if (this.chatsOpen[chatUuid]) {
        await this.cleanUpAfterReassign({ chatUuid });
      }

      const chat = {
        chatUuid,
        lastForm: null,
        groups,
        ...JSON.parse(data),
      };

      if (chat.conversationLanguage) chat.conversationLanguage = normalizeLanguageCode(chat.conversationLanguage);

      const lastForm = await this.populateCompletedForm(chat);
      this.playAudioNewChat();

      if (!this.isTabVisible && this.activeHubSettings.notificationBrowser) {
        useNotificationNativeStore().showNotificationNative({
          title: i18n.global.t('settingsLivechat.notifications.newMessageTitle'),
          body: i18n.global.t('settingsLivechat.notifications.newMessageBody'),
          // `silent` isn't in NativeNotificationPayload (unused by the receiving action) but was
          // passed by legacy; cast to preserve the exact payload shape without touching that store.
          silent: false,
        } as any);
      }

      this.setChatsUnassigned({ ...this.chatsUnassigned, [chatUuid]: { ...chat, lastForm } });
    },
    /**
     * Fetches and populates (mutates object) the most recently completed form into this chat instance
     * @param {Object} chat
     * @returns {Promise<RecentlyCompletedForm>}
     */
    async populateCompletedForm(chat: any) {
      /**
       * Get the display name of the channel
       * @param {RecentlyCompletedFormBot} bot
       * @returns {string}
       */
      const getChannelDisplayName = (bot: any) => {
        const details = useBotsStore().getBot(bot.botId);
        return details.channels.find((ch: any) => ch.channelId === bot.channelId)?.displayName || bot.channelId;
      };

      const forms = await BotFormsService.searchCompleted(chat.uniqueBotId, chat.uniqueUserId);

      // Legacy tolerated a `null` result here (would throw at runtime just like this does);
      // non-null assertion preserves that exact behavior instead of adding a new guard.
      if (forms![0]) {
        forms![0].bot.channelDisplay = getChannelDisplayName(forms![0].bot);
      }
      // chat.lastForm = forms[0];
      return forms![0];
    },
    // @ chat
    async startChat({ uuid, uniqueUserId }: { uuid: string; uniqueUserId: string }) {
      const chat = await LivechatService.assignChat({
        chatUuid: uuid,
        uniqueUserId,
        agentUuid: this.agent.uuid,
      });
    },
    // Next line has nothing to deal with sate activeChat
    async changeToChat({ chatUuid }: { chatUuid: string }) {
      try {
        const authStore = useAuthStore();
        const isSupervisor = authStore.isSupervisor;
        let chat = null;
        if (this.agentStatus === statusTypes.online) {
          chat = this.chatsOpen[chatUuid] || this.chatsUnassigned[chatUuid] || this.chatsClosed[chatUuid];
        }

        if (this.agentStatus === statusTypes.offline && isSupervisor) {
          chat = this.chatsOtherAgents[chatUuid] || this.chatsClosed[chatUuid];
        }

        if (!chat) {
          return;
        }

        await this.loadChatHistory({
          chatUuid,
          uniqueBotId: this.uniqueBotId,
          uniqueUserId: chat.uniqueUserId,
        });

        this.activeChatId = chatUuid;
        this.activeChatLanguage = normalizeLanguageCode(chat.conversationLanguage);
        chat.isMessageNew = false;
        // In case if we need to store the whole chat object, just use data: chat
        this.setLocalChatsInfo({ chat: chatUuid, data: { isMessageNew: false } });

        if (this.useTranslation) {
          //translate chat history
          const untranslatedMessages: Record<string, any> = {};

          // there are probably already translated messages
          // check which are translated and request a translation for the rest
          const allMsgs = await this.prepareActiveChatMessages();
          Object.values(allMsgs).forEach((message: any) => {
            if (!this.chatTranslations[message.data]) {
              untranslatedMessages[message.data] = message;
            }
          });

          this.translate({ messages: Object.values(untranslatedMessages), language: this.agentLanguages[0] });
        }

        this.setIsChatOpen(true);
      } catch (error) {
        console.error('Failed to change to chat:', error);
      }
    },

    prepareActiveChatMessages() {
      const activeChat = this.activeChat;
      const historyMsgs = this.chatHistoryById(activeChat.uuid).map((m: any) => toProperMsgFormat(m, activeChat));

      const chatMsgs = activeChat.messages.map((m: any) => toProperMsgFormat(m, activeChat));
      return [...historyMsgs, ...chatMsgs];
    },
    // @ chat
    async closeChat({ uuid, uniqueUserId, isStaging }: { uuid: string; uniqueUserId: string; isStaging?: boolean }) {
      const closedChat = await LivechatService.closeChat({
        chatUuid: uuid,
        uniqueUserId,
        isStaging,
      });
      if (closedChat.error) {
        console.error('closeChat', closedChat.error);
        return;
      }
      this.cleanupClosedChat({ uuid });
    },

    cleanupClosedChat({ uuid }: { uuid: string }) {
      this.removeLocalChatInfo({ chat: uuid });
      const _chatsOpen = { ...this.chatsOpen };
      delete _chatsOpen[uuid];
      this.setChatsOpen(_chatsOpen);
      if (this.activeChatId === uuid) this.activeChatId = null;
      const _chatHistories = { ...this.chatHistories };
      delete _chatHistories[uuid];
      this.chatHistories = _chatHistories;
      this.loadClosedChats();
    },

    async closeChatSupervisor({ uuid }: { uuid: string }) {
      const closedChat = await LivechatService.closeChatSupervisor({
        chatUuid: uuid,
      });
      if (closedChat.error) {
        console.error('closeChat', closedChat.error);
        return;
      }
      this.removeLocalChatInfo({ chat: uuid });
      await this.loadChatsOrganization();
    },

    async handleChatClosedBySupervisor({ uuid }: { uuid: string }) {
      // Notify the agent that the chat has been closed by the supervisor
      this.setIsClosedBySupervisor(true);

      // cleanup closed chat
      this.cleanupClosedChat({ uuid });
    },

    async closeAllOpenChats() {
      const chatUuids = Object.keys(this.chatsOpen);
      const closeChatPromises = chatUuids.map((chatUuid) =>
        this.closeChat({ uuid: chatUuid, uniqueUserId: this.chatsOpen[chatUuid].uniqueUserId }),
      );
      await Promise.all(closeChatPromises);
    },
    async assignChat({ chatUuid, data }: { chatUuid: string; data: string }) {
      const { uniqueUserId, groups } = this.chatsUnassigned[chatUuid];
      const parsedData = JSON.parse(data);

      // Re-fetch form information for this chat
      const lastForm = await this.populateCompletedForm(parsedData);
      parsedData.lastForm = lastForm;

      if (parsedData.agent.uuid !== this.agent.uuid) {
        //chat is assigned to another agent => move chat to chats
        const chat = { ...parsedData };
        const _chatsUnassigned = { ...this.chatsUnassigned };
        delete _chatsUnassigned[chatUuid];
        this.setChatsUnassigned(_chatsUnassigned);
        this.setChatsOtherAgents({ ...this.chatsOtherAgents, [chatUuid]: chat });
      } else {
        //chat is assigned to current agent => change to chat
        const chat = {
          status: chatStatus.open,
          ...parsedData,
          groups,
          agent: this.agent._id,
        };

        this.setChatsOpen({ ...this.chatsOpen, [chatUuid]: chat });
        const _chatsUnassigned = { ...this.chatsUnassigned };
        delete _chatsUnassigned[chatUuid];
        this.setChatsUnassigned(_chatsUnassigned);

        // `changeToChat` only destructures `chatUuid` (matches legacy `dispatch('changeToChat', { chatUuid, uniqueUserId })`,
        // which likewise ignored `uniqueUserId`); cast preserves the exact extra-prop payload shape.
        this.changeToChat({ chatUuid, uniqueUserId } as any);
      }
    },

    async reassignChat({ chatUuid }: { chatUuid: string }) {
      // if the chatUuid is in the open chats -> the chat is reassigned to another agent -> clean up the chat
      if (this.chatsOpen[chatUuid]) {
        return this.cleanUpAfterReassign({ chatUuid });
      }

      // load chat
      const reassignedChat = await LivechatService.getChatByUuid({ chatUuid });

      // Re-fetch form information for this chat
      const lastForm = await this.populateCompletedForm(reassignedChat);
      reassignedChat.lastForm = lastForm;

      //chat is assigned to current agent => change to chat
      const chat = {
        status: chatStatus.open,
        ...reassignedChat,
        agent: this.agent._id,
      };

      this.setChatsOpen({ ...this.chatsOpen, [chatUuid]: chat });
      // const _chatsUnassigned = { ...this.chatsUnassigned };
      // delete _chatsUnassigned[chatUuid];
      // this.setChatsUnassigned(_chatsUnassigned);

      // this.changeToChat({ chatUuid, uniqueUserId });

      useNotificationNativeStore().showNotificationNative({
        title: i18n.global.t('settingsLivechat.notifications.newMessageTitle'),
        body: i18n.global.t('settingsLivechat.notifications.newMessageBody'),
        // `silent` isn't in NativeNotificationPayload (unused by the receiving action) but was
        // passed by legacy; cast to preserve the exact payload shape without touching that store.
        silent: false,
      } as any);
    },

    cleanUpAfterReassign({ chatUuid }: { chatUuid: string }) {
      this.removeLocalChatInfo({ chat: chatUuid });
      const _chatsOpen = { ...this.chatsOpen };
      delete _chatsOpen[chatUuid];
      this.setChatsOpen(_chatsOpen);
      if (this.activeChatId === chatUuid) this.activeChatId = null;
      const _chatHistories = { ...this.chatHistories };
      delete _chatHistories[chatUuid];
      this.chatHistories = _chatHistories;
    },

    async addMessage(payload: any) {
      const { chatUuid, data, type, sender, created_at, attachments } = payload;
      const chat = this.chatsOpen[chatUuid];

      if (!chat) return;

      const isAgent = sender.role === 'agent';
      const isActiveChat = this.activeChatId === chatUuid;

      if (isAgent) {
        // Change timestamp only if last message was sent by agent
        chat.updated_at = created_at;
        chat.isMessageNew = false;
        // In case if we need to store the whole chat object, just use data: chat
        this.setLocalChatsInfo({ chat: chatUuid, data: { isMessageNew: false } });
        if (this.useTranslation && data) {
          // The input already translated this text for the preview, so reuse it: translating again
          // costs a second request and can return a different wording than the agent just approved.
          const preTranslated = payload.translation?.original === data ? payload.translation.translated : null;
          if (preTranslated) {
            this.addTranslatedMessage({
              language: chat.conversationLanguage,
              original: preTranslated,
              translated: data,
            });
            payload.data = preTranslated;
          } else {
            //translate outgoing message
            const hadTranslationErrorsAlready = this.translationServiceError;
            const translations = await this.translate({
              messages: [payload],
              language: chat.conversationLanguage,
              reversed: true,
            });
            if (this.translationServiceError && !hadTranslationErrorsAlready) {
              throw new Error('Translation service error');
            }
            if (translations?.length) {
              payload.data = translations[0];
            }
          }
        }
        // Local only, the livechat server has no use for it
        delete payload.translation;
      } else if (this.useTranslation) {
        this.translate({ messages: [payload], language: this.agentLanguages[0] });
      }

      /**
       * Mirrors how livechat stores it: the agent's own files each become a message of their own
       * with the file url as its text, while a file the visitor sent — an upload, or the recording
       * `data` was transcribed from — has no url to use that way and stays with the text it
       * arrived with.
       */
      const messageAttachments = Array.isArray(attachments) ? attachments : [];
      const splitAttachments = messageAttachments.filter((attachment: any) => attachment.url);
      const keptAttachments = messageAttachments.filter((attachment: any) => !attachment.url);

      for (const attachment of splitAttachments) {
        chat.messages.unshift({
          sender,
          type,
          data: attachment.url,
          created_at,
          updated_at: created_at,
          chatUuid,
          attachments: [attachment],
        });
      }

      if (data || keptAttachments.length) {
        // Add message to the end of the dialog (view)
        chat.messages.unshift({
          sender,
          type,
          data: payload.data,
          created_at,
          updated_at: created_at,
          chatUuid,
          attachments: keptAttachments,
        });
      }

      if (!isAgent && (!isActiveChat || !this.activeChatIsVisible)) {
        chat.isMessageNew = true;
        // In case if we need to store the whole chat object, just use data: chat
        this.setLocalChatsInfo({ chat: chat.uuid, data: { isMessageNew: true } });
        chat.updated_at = created_at;
        this.playAudioNewMessage();
        if (!this.isTabVisible && this.activeHubSettings.notificationBrowser) {
          useNotificationNativeStore().showNotificationNative({
            title: i18n.global.t('settingsLivechat.notifications.newMessageTitle'),
            body: i18n.global.t('settingsLivechat.notifications.newMessageBody'),
          });
        }
      }

      this.setChatsOpen({ ...this.chatsOpen, [chat.uuid]: chat });

      primus.write(payload);
    },

    /**
     * Returns Chat Archive in HTML format (as default)
     * Modify if file is needed
     * @param {Object} params
     * @param {Object} params.uniqueBotId - Bot ID
     * @param {Object} params.uniqueUserId - Unique User ID
     * @returns {string} - HTML
     */
    async getChatArchive({ uniqueBotId, uniqueUserId }: { uniqueBotId: string; uniqueUserId: string }) {
      const historyHtml = await LivechatService.getChatArchive({ botId: uniqueBotId, uniqueUserId });
      return historyHtml;
    },

    /**
     * Sends Archive PDF file via Email
     * @param {Object} params
     * @param {Object} params.uniqueBotId - Bot ID
     * @param {Object} params.uniqueUserId - Unique User ID
     * @returns {Promise{ success?: boolean, error?: any }}
     */
    async sendEmailArchive(params: any) {
      return await LivechatService.sendEmailArchive(params);
    },

    async addWebChatCmd(payload: any) {
      const activeChatUuid = this.activeChatId as any;

      const chat = this.chatsOpen[activeChatUuid];
      if (!chat) return;

      const data = {
        type: 'webchat_cmd',
        data: payload,
        agentUuid: this.agent.uuid,
        chatUuid: activeChatUuid,
        sender: {
          role: 'agent',
          uniqueId: this.agent.uuid,
        },
      };

      primus.write(data);
    },

    async userStatusChanged({
      chatUuid,
      data,
      created_at,
      type,
      sender,
    }: {
      chatUuid: string;
      data: any;
      created_at: any;
      type: string;
      sender: any;
    }) {
      const chat = this.chatsOpen[chatUuid];
      if (!chat) return;

      const isOnline = type === 'user_connected';
      if (chat.user) {
        chat.user.status = isOnline ? 'online' : 'offline';
        if (!isOnline) chat.user.lastSeenAt = created_at;
      }

      chat.messages.unshift({
        sender,
        type,
        data,
        created_at,
        updated_at: created_at,
        chatUuid,
      });

      this.setChatsOpen({ ...this.chatsOpen, [chatUuid]: chat });
    },
    async handleAgentOnline({ data, sender }: { data: string; sender?: any }) {
      try {
        const agent = JSON.parse(data);

        // Don't add current agent to the list
        if (agent.uuid === this.agent?.uuid) {
          return;
        }

        // Check if agent is already in the list
        const existingAgentIndex = this.onlineAgents.findIndex((a: any) => a.uuid === agent.uuid);

        if (existingAgentIndex === -1) {
          // Add new agent to the list
          const updatedAgents = [...this.onlineAgents, agent];
          this.setOnlineAgents(updatedAgents);
          console.log(`Agent ${agent.name} (${agent.uuid}) came online`);
        } else {
          // Update existing agent data
          const updatedAgents = [...this.onlineAgents];
          updatedAgents[existingAgentIndex] = agent;
          this.setOnlineAgents(updatedAgents);
          console.log(`Updated online agent ${agent.name} (${agent.uuid})`);
        }
      } catch (error) {
        console.error('Error handling agent online event:', error);
      }
    },
    async handleAgentOffline({ data, sender }: { data: string; sender?: any }) {
      try {
        const agent = JSON.parse(data);

        // Remove agent from the online list
        const updatedAgents = this.onlineAgents.filter((a: any) => a.uuid !== agent.uuid);
        this.setOnlineAgents(updatedAgents);
        console.log(`Agent ${agent.name} (${agent.uuid}) went offline`);
      } catch (error) {
        console.error('Error handling agent offline event:', error);
      }
    },
    async updateAgent({
      name,
      profilePicture,
      hubSettings,
      title,
      languages,
      isAgentSelf = false,
    }: {
      name?: string;
      profilePicture?: string;
      hubSettings?: any;
      title?: string;
      languages?: string[];
      isAgentSelf?: boolean;
    }) {
      const updatedAgent = await LivechatService.updateAgent({
        uuid: this.agent.uuid,
        name,
        profilePicture,
        hubSettings,
        title,
        languages,
        isAgentSelf,
      });

      if (isAgentSelf) {
        this.setAgent(updatedAgent);
      }

      return true;
    },
    async goOnline() {
      if (!primus) {
        if (!this.isCurrentTabActive) {
          this.activateCurrentTab();
        }
        await this.initChat();
        await this.loadOnlineAgents();
        useInactivityTrackerStore().restartTimeoutTimer();
      }
    },
    async goOffline(payload?: { broadcast?: boolean }) {
      primusGeneration++;
      if (primus || primusConnecting) {
        primus?.end();
        primus = null;
        if (this.isCurrentTabActive && payload?.broadcast !== false) {
          this.deactivateCurrentTab();
        }
      }
      this.isAgentInactive = false;
      // Clear online agents when going offline
      this.setOnlineAgents([]);
      useInactivityTrackerStore().removeTimeoutTimer();
    },
    loadTabId() {
      let tabIdSession = getFromStorage(tabIdSessionKey, null, 'session');

      if (!tabIdSession) {
        tabIdSession = randomString();
        setToStorage(tabIdSessionKey, tabIdSession, 'session');
      }

      this.setCurrentTabId(tabIdSession);
    },
    registerTabClosed() {
      if (this.isCurrentTabActive) {
        broadcastChannel?.postMessage({ activeTab: null });
      }
    },
    activateCurrentTab() {
      // check if current tab is not active
      if (this.currentTabId !== this.activeTabId) {
        // save the changes
        const currentTab = this.currentTabId;
        broadcastChannel?.postMessage({ activeTab: currentTab });
        this.setActiveTabId(this.currentTabId);
        useInactivityTrackerStore().restartTimeoutTimer();
      }
    },
    deactivateCurrentTab() {
      broadcastChannel?.postMessage({ activeTab: null });
      this.setActiveTabId(null);
      useInactivityTrackerStore().removeTimeoutTimer();
    },
    handleBroadcastChannelMessage(ev: MessageEvent) {
      const data = ev.data;
      const newActiveTabId = data?.activeTab;

      //if the current tab is becoming active don't do anything
      if (newActiveTabId && newActiveTabId === this.currentTabId) return;

      // if another tab has stolen the session
      if (this.isCurrentTabActive) {
        this.goOffline({ broadcast: false });
      }

      this.setActiveTabId(newActiveTabId);
    },
    playAudioNewMessage() {
      if (!this.activeHubSettings?.notificationAudio) {
        return;
      }
      useAudioStore().playAudio({
        url: this.activeHubSettings?.soundNewMessage,
        volume: this.activeHubSettings?.volume,
      });
    },
    playAudioNewChat() {
      if (!this.activeHubSettings?.notificationAudio) {
        return;
      }
      useAudioStore().playAudio({
        url: this.activeHubSettings?.soundNewChat,
        volume: this.activeHubSettings?.volume,
      });
    },
    async loadChatHistory({
      uniqueUserId,
      uniqueBotId,
      chatUuid,
    }: {
      uniqueUserId: string;
      uniqueBotId: string | null;
      chatUuid: string;
    }) {
      if (this.chatHistories[chatUuid]) {
        return true;
      }

      const result = await DialogService.getChat(uniqueUserId, uniqueBotId as any);
      if (!result?.chat) {
        // Legacy comma-expression bug preserved verbatim (evaluates to `uniqueBotId` only,
        // `uniqueUserId` is discarded); `void` marks the discard as intentional to satisfy TS
        // without changing the logged value.
        console.error(`No chat history found (${(void uniqueUserId, uniqueBotId)})`);
      }
      this.chatHistories[chatUuid] = result.chat;

      return true;
    },
    /**
     * Merged from legacy action `setIsLivechatVisible` (which committed the mutation of the same
     * name, plus additional logic to mark the active chat read) + legacy mutation
     * `setIsLivechatVisible`.
     */
    setIsLivechatVisible(isVisible: boolean) {
      this.isLivechatVisible = isVisible;

      if (this.activeChatIsVisible && this.activeChatId) {
        this.chatsOpen[this.activeChatId].isMessageNew = false;
        // In case if we need to store the whole chat object, just use data: this.chatsOpen[this.activeChatId]
        this.setLocalChatsInfo({ chat: this.activeChatId, data: { isMessageNew: false } });
      }
    },
    /**
     * @typedef {object} TranslationPayload
     * @property {object[]} messages - Array of messages to translate
     * @property {string} language - The language to translate to
     * @property {boolean} reversed - If true, the translation will be from the agent language to the original language
     *
     */
    /**
     *
     * @param {TranslationPayload} translationPayload
     * @returns
     */
    /**
     * Translates texts with the engine the bot is configured for and returns them in the order they
     * were sent. The single entry point for every livechat translation - input preview, history and
     * sent messages - so an agent can never see a preview from one engine and send the wording of
     * another.
     *
     * @returns Translations in the order of `texts`, or null if translating failed
     */
    async translateTexts({
      texts,
      language,
      actionName = 'livechat',
    }: {
      texts: string[];
      language: string;
      actionName?: string;
    }): Promise<string[] | null> {
      if (!texts?.length || !language) return null;
      try {
        let translations: (string | undefined)[] | undefined;
        if (this.useDeepL) {
          const response = await TranslationService.getTranslations(this.uniqueBotId as any, texts, language);
          translations = response?.translation?.map((t: any) => t.text);
        } else {
          // The LLM keeps the structure it was given, so a list of messages comes back as a list
          const message = texts.map((text) => ({ text }));
          const res = await TranslationService.getTranslationLLM({
            actionName,
            botId: this.uniqueBotId as any,
            language,
            message,
          });
          if (!res.success) throw new Error(`Translation failed: ${JSON.stringify(res.error)}`);
          // Keep the original where the model dropped a text, an empty message is worse than an untranslated one
          translations = res.data?.message?.map((m: any, i: number) => m?.text || texts[i]);
        }

        console.log(`translations useDeepL (${this.useDeepL})`, translations);

        // A missing or extra translation would land on the wrong message
        if (translations?.length !== texts.length) throw new Error('Translation does not match the sent texts');

        this.translationServiceError = false;
        return translations as string[];
      } catch (error) {
        console.error('translateTexts:', error);
        this.translationServiceError = true;
        return null;
      }
    },
    async translate({
      messages,
      language,
      reversed = false,
    }: {
      messages: any[];
      language: string;
      reversed?: boolean;
    }) {
      if (!messages || messages.length === 0 || !this.useTranslation || messages.every((m) => typeof m === 'string'))
        return;
      const texts = messages.map(extractMsgText);
      const translations = await this.translateTexts({
        texts,
        language,
        actionName: reversed ? 'livechat_agent' : 'livechat_user',
      });
      if (!translations) return;

      messages.forEach((message, index) => {
        this.addTranslatedMessage({
          language,
          // usually the incoming message will be translated to the agent language
          // and the translation will be stored in `translated` and the original message in `original`
          // if reversed is true, the original message will be stored in `translated` and the translation in `original`
          // this is useful when translating outgoing messages from the agent language to the original language
          // thus ensuring that in the chat translations the original message is always the one that the user sees
          // and the translated message is the one that the agent sees
          original: reversed ? translations[index] : message.data,
          translated: reversed ? message.data : translations[index],
        });
      });
      return translations;
    },
    async createLivechat({ uniqueBotId }: { uniqueBotId: string }) {
      const { exists } = (await LivechatService.getBotHasLivechat(uniqueBotId)) || false;

      this.botHasLivechat = exists || false;

      if (!this.botHasLivechat) {
        // create org without agent, agent will be added later
        const organisation = await LivechatService.createOrganization({
          uniqueBotId,
        });

        if (organisation) {
          this.botHasLivechat = true;
          return organisation;
        }

        return false;
      }

      return this.organisation;
    },
    setAgentInactive() {
      // if the agent is active in the current tab, go offline
      if (this.isCurrentTabActive) {
        this.isAgentInactive = true;
      }
    },
    resetAgentInactive() {
      this.isAgentInactive = false;
    },
    async addNoteToChat({ chatUuid, note }: { chatUuid: string; note: any }) {
      const chat = this.chatsOpen[chatUuid];
      const newNote = await LivechatService.addNoteToChat(chatUuid, note);
      chat.agentNotes = chat.agentNotes || [];
      chat.agentNotes.push(newNote);
      this.setChatsOpen({ ...this.chatsOpen, [chatUuid]: chat });
    },

    async removeNoteFromChat({ chatUuid, noteId }: { chatUuid: string; noteId: string }) {
      const chat = this.chatsOpen[chatUuid];
      try {
        await LivechatService.removeNoteFromChat(chatUuid, noteId);
        chat.agentNotes = chat.agentNotes.filter((note: any) => note._id !== noteId);
        this.setChatsOpen({ ...this.chatsOpen, [chatUuid]: chat });
      } catch (error) {
        console.error('removeNoteFromChat', error);
      }
    },

    async addTagToChat({ chatUuid, tag }: { chatUuid: string; tag: string }) {
      if (this.activeChatId !== chatUuid) {
        console.error('Cannot add tag to chat that is not active');
        return;
      }
      const result = await LivechatService.addTagToChat(chatUuid, tag);
      if (result.error) {
        console.error('Error adding tag:', result.error);
        return;
      }
      let chat = this.activeChat;
      if (chat) {
        chat.tags = chat.tags || [];
        if (!chat.tags.includes(tag)) {
          chat.tags.push(tag);
        }
      }
    },

    async removeTagFromChat({ chatUuid, tag }: { chatUuid: string; tag: string }) {
      if (this.activeChatId !== chatUuid) {
        console.error('Cannot remove tag from chat that is not active');
        return;
      }
      const result = await LivechatService.removeTagFromChat(chatUuid, tag);
      if (result.error) {
        console.error('Error removing tag:', result.error);
        return;
      }
      let chat = this.activeChat;
      if (chat) {
        chat.tags = chat.tags || [];
        chat.tags = chat.tags.filter((t: string) => t !== tag);
      }
    },

    closeIsClosedBySupervisorModal() {
      this.setIsClosedBySupervisor(false);
    },

    /** Converted from legacy mutation `setValue` (no matching legacy action existed). Generic single-key state setter. */
    setValue({ key, value }: { key: string; value: any }) {
      (this as any)[key] = value;
    },
    setIsChatOpen(isChatOpen: boolean) {
      this.isChatOpen = isChatOpen;
    },
    setCurrentTabId(tabId: string) {
      this.currentTabId = tabId;
    },
    setActiveTabId(activeTabId: string | null) {
      this.activeTabId = activeTabId;
    },
    setAgent(agent: any) {
      this.agent = agent;
      this.agentLanguages = agent?.languages?.length ? agent?.languages : this.agentLanguages;
    },
    setAgentForOrganizationStatus(status: string) {
      this.agentForOrganizationStatus = status;
    },
    setOnlineAgents(agents: any[]) {
      this.onlineAgents = agents;
    },
    setChatsUnassigned(chats: Record<string, any>) {
      this.chatsUnassigned = chats;
    },
    setChatsOpen(chats: Record<string, any>) {
      this.chatsOpen = chats;
    },
    setChatsOtherAgents(chats: Record<string, any>) {
      this.chatsOtherAgents = chats;
    },
    setChatsClosed(chats: Record<string, any>) {
      this.chatsClosed = chats;
    },
    setChatTranslations(translations: Record<string, string>) {
      this.chatTranslations = translations;
    },
    addTranslatedMessage({
      language,
      original,
      translated,
    }: {
      language: string;
      original: string;
      translated: string;
    }) {
      this.chatTranslations = {
        ...this.chatTranslations,
        [original]: translated,
      };
    },
    resetLivechatChats() {
      this.chatsClosed = {};
      this.chatsOpen = {};
      this.chatsOtherAgents = {};
      this.chatsUnassigned = {};
      this.activeChatId = null;
      this.isChatOpen = false;
      this.isLivechatVisible = false;
      this.chatTranslations = {};
      this.chatHistories = {};
      this.localChatsInfo = {};
    },
    resetLivechat() {
      this.agent = null;
      this.organisation = null;
      this.onlineAgents = [];
      this.chatsClosed = {};
      this.chatsOpen = {};
      this.chatsOtherAgents = {};
      this.chatsUnassigned = {};
      this.activeChatId = null;
      this.isChatOpen = false;
      this.isLivechatVisible = false;
      // this.botHasLivechat = false;
      this.primusInitialized = false;
      this.chatTranslations = {};
      this.chatHistories = {};
      this.localChatsInfo = {};
      this.isAgentInactive = false;
    },

    /**
     * @param {object} param
     * @param {string} param.chat - Unique Chat ID (uuid)
     * @param {string} param.data - Object that which includes new Values to be saved locally for chat
     * @returns {void}
     */
    setLocalChatsInfo({ chat, data }: { chat: string; data: Record<string, any> }) {
      if (!chat) return;

      const local = this.localChatsInfo[chat] ? _.cloneDeep(this.localChatsInfo[chat]) : {};

      // Upsert or overwrite
      this.localChatsInfo[chat] = _.cloneDeep({ ...local, ...data });
    },
    /**
     * @param {{
     *  chat: string,
     * }} param
     * @returns {void}
     */
    removeLocalChatInfo({ chat }: { chat: string }) {
      if (this.localChatsInfo[chat]) {
        const newLocalChatsInfo = { ...this.localChatsInfo };
        delete newLocalChatsInfo[chat];
        this.localChatsInfo = newLocalChatsInfo;
      }
    },
    setIsClosedBySupervisor(isClosedBySupervisorOpen: boolean) {
      this.isClosedBySupervisorOpen = isClosedBySupervisorOpen;
    },
  },
  vuexPersist: {
    local: true,
    session: true,
  },
});
